import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  displayName: string;
  handle?: string;
  bio?: string;
  boatName?: string;
  boatType?: string;
  boatSize?: string;
  mooringStatus?: string;
  role: 'user' | 'admin';
  status: string;
  themePreference: 'light' | 'dark' | 'auto';
  locationOptIn: boolean;
  avatarMediaId?: string;
  createdAt: string;
  isSubscribed?: boolean;
  hasPassword?: boolean;
  hasPasskey?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setAuth: (token: string, user: User) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: true,
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setAuth: (token, user) => set({ token, user }),
      setLoading: (isLoading) => set({ isLoading }),
      logout: () => set({ user: null, token: null }),
    }),
    {
      name: 'moorly-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);

interface ThemeState {
  theme: 'light' | 'dark' | 'auto';
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'auto',
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
    }),
    {
      name: 'moorly-theme',
    }
  )
);

function applyTheme(theme: 'light' | 'dark' | 'auto') {
  const root = document.documentElement;
  
  if (theme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

function readPersistedTheme(): 'light' | 'dark' | 'auto' {
  if (typeof window === 'undefined') return 'auto';
  const raw = window.localStorage.getItem('moorly-theme');
  if (!raw) return 'auto';
  try {
    const parsed = JSON.parse(raw) as { state?: { theme?: unknown } };
    const theme = parsed?.state?.theme;
    return theme === 'light' || theme === 'dark' || theme === 'auto' ? theme : 'auto';
  } catch {
    return 'auto';
  }
}

if (typeof window !== 'undefined') {
  // Initialize theme on load
  applyTheme(readPersistedTheme());

  // Listen for system theme changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const onThemeChange = () => {
    const currentTheme = useThemeStore.getState().theme;
    if (currentTheme === 'auto') {
      applyTheme('auto');
    }
  };

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onThemeChange);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(onThemeChange);
  }
}

interface NotificationState {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  unreadCount: 0,
  setUnreadCount: (unreadCount) => set({ unreadCount }),
}));
